import type { VideoObject } from "../types";
import { __APP_HOST_PUBLIC__ } from "@/constants/app";

/**
 * Extracts embed URL from iframe HTML string
 */
function extractEmbedUrl(embedHtml: string | undefined): string | undefined {
  if (!embedHtml) return undefined;

  const regex = /src="([^"]*)"/;
  const match = embedHtml.match(regex);
  return match ? match[1] : undefined;
}

/**
 * Parameters matching the Video component props
 */
export interface VideoSchemaParams {
  title: string;
  caption?: string;
  videoType: "asset" | "youtube" | "icareus";
  video?: Wif.Mgnl.Video;
  image?: Wif.Mgnl.Image;
  youtubeVideoUrl?: string;
  icareus?: string;
}

/**
 * Generates VideoObject schema for a Video component
 * Returns null if required fields are missing
 */
export function generateVideoObjectSchema(
  params: VideoSchemaParams
): VideoObject | null {
  const { title, caption, videoType, video, image, youtubeVideoUrl, icareus } =
    params;

  // Title is required for video schema
  if (!title) {
    return null;
  }

  // Get thumbnail URL from image
  let thumbnailUrl: string | undefined;
  if (image?.["@link"]) {
    thumbnailUrl = __APP_HOST_PUBLIC__ + image["@link"];
  }

  // Build base schema
  const schema: VideoObject = {
    "@type": "VideoObject",
    name: title,
  };

  // Add description if available
  if (caption) {
    schema.description = caption;
  }

  // Add thumbnail if available
  if (thumbnailUrl) {
    schema.thumbnailUrl = thumbnailUrl;
  }

  // Add video-specific URLs based on type
  if (videoType === "youtube" && youtubeVideoUrl) {
    const embedUrl = youtubeVideoUrl.replace("watch?v=", "embed/");
    schema.embedUrl = embedUrl;
  } else if (videoType === "icareus" && icareus) {
    const embedUrl = extractEmbedUrl(icareus);
    if (embedUrl) {
      schema.embedUrl = embedUrl;
    }
  } else if (videoType === "asset" && video) {
    if (video["@link"]) {
      schema.contentUrl = __APP_HOST_PUBLIC__ + video["@link"];
    }

    // Add encoding format if available
    if (video.metadata?.mimeType) {
      schema.encodingFormat = video.metadata.mimeType;
    }
  }

  // Add publisher (Business Finland)
  schema.publisher = {
    "@type": "Organization",
    name: "Business Finland",
  };

  return schema;
}

/**
 * Converts VideoObject schema to JSON-LD string
 */
export function videoSchemaToJsonLd(schema: VideoObject | null): string {
  if (!schema) return "";

  const schemaWithContext = {
    "@context": "https://schema.org" as const,
    ...schema,
  };

  return JSON.stringify(schemaWithContext);
}
